import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

// ====== ENV ======
const ENV_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.API_BASE) ||
  `${API_BASE_URL}/api`;

const ENV_HOST =
  process.env.REACT_APP_HOST ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.HOST) ||
  API_BASE_URL;

const HOST = ENV_HOST;
const DEFAULT_AVATAR = `${HOST}/uploads/athletes/default.jpg`;

const api = axios.create({
  baseURL: ENV_BASE,
  withCredentials: true,
  timeout: 15000,
});

// Normaliza '/trainer/' → '/trainers/'
api.interceptors.request.use((cfg) => {
  if (typeof cfg.url === 'string') cfg.url = cfg.url.replace(/^\/trainer\//, '/trainers/');
  return cfg;
});

// ===== Utils
const normSport = (v) => {
  const s = String(v || '').toLowerCase();
  if (['run','running','correr'].includes(s)) return 'correr';
  if (['bike','cycling','ciclismo'].includes(s)) return 'ciclismo';
  if (['swim','swimming','nadar','natación','natacion'].includes(s)) return 'nadar';
  if (['tri','triatlon','triatlón','triathlon'].includes(s)) return 'triatlon';
  if (['strength','fuerza','gym','gimnasio'].includes(s)) return 'fuerza';
  return 'otro';
};

const resolveAthleteAvatar = (a) => {
  const cands = [
    a.__photoResolved, a.profilePhoto, a.profilePicture, a.photoUrl, a.avatarUrl, a.avatar, a.imageUrl, a.picture, a.photo,
    a?.athlete?.profilePhoto, a?.athlete?.photoUrl, a?.user?.photoUrl,
  ];
  for (const raw of cands) {
    const v = (raw ?? '').toString().trim();
    if (!v) continue;
    if (v.startsWith('http')) return v;
    if (v.startsWith('/')) return `${HOST}${v}`;
    if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?.*)?$/.test(v) || v.includes('/uploads/')) {
      return `${HOST}/uploads/athletes/${v}`;
    }
  }
  return DEFAULT_AVATAR;
};

// ===== API helpers
async function getGroups(trainerId, signal) {
  const tries = [
    () => api.get(`/trainers/${trainerId}/groups`, { signal }),
    () => api.get(`/groups`, { params: { trainerId }, signal }),
    () => api.get(`/groups/list`, { params: { trainerId }, signal }),
    () => api.get(`/trainer/${trainerId}/groups`, { signal }),
    () => api.get(`/trainer/groups`, { params: { trainerId }, signal }),
  ];
  for (const t of tries) {
    try {
      const { data } = await t();
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.groups) ? data.groups : []);
      return arr.map((g) => ({
        _id: String(g._id || g.id || g.groupId || g.group_id),
        name: (g.name || g.title || 'Grupo').trim(),
        sport: normSport(g.sport || g.type),
        members: Array.isArray(g.members) ? g.members.map(String)
          : Array.isArray(g.athletes) ? g.athletes.map(a => String(a?._id || a)) : [],
      })).filter(x => x._id);
    } catch {}
  }
  return [];
}

export default function DashboardGroupsSidebar({ trainerId, athletes = [], onAddGroup }) {

  const { t } = useTranslation();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [athletesFilter, setAthletesFilter] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!trainerId) return;
    const ctrl = new AbortController();
    (async () => {
      setLoading(true); setErr('');
      try { setGroups(await getGroups(trainerId, ctrl.signal)); }
      catch { setErr(t('trainerDashboard.errorLoadingGroups')); setGroups([]); }
      finally { setLoading(false); }
    })();
    return () => ctrl.abort();
  }, [trainerId, t]);

  const athIdx = useMemo(() => {
    const m = new Map();
    athletes.forEach(a => {
      const id = String(a._id || a.id || '');
      if (!id) return;
      const name = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || t('trainerDashboard.athlete');
      m.set(id, { id, name, avatar: resolveAthleteAvatar(a) });
    });
    return m;
  }, [athletes, t]);

  const allAthletes = useMemo(() => {
    return athletes
      .map(a => {
        const id = String(a._id || a.id || '');
        const name = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || t('trainerDashboard.athlete');
        return id ? { id, name, avatar: resolveAthleteAvatar(a) } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }, [athletes, t]);

  const filteredAll = useMemo(() => {
    const q = athletesFilter.trim().toLowerCase();
    return q ? allAthletes.filter(a => a.name.toLowerCase().includes(q)) : allAthletes;
  }, [allAthletes, athletesFilter]);

  return (
    <div>
      {/* Grupos */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
        <strong style={{ fontSize:13 }}>
          {t('trainerDashboard.groups')}
          <span style={{ background:'#e5e7eb', borderRadius:999, padding:'1px 7px', fontSize:11, fontWeight:700, color:'#374151', marginLeft:4 }}>{groups.length}</span>
        </strong>
        {onAddGroup && (
          <button
            style={{ borderRadius:999, border:'1px solid #e5e7eb', background:'#f9fafb', color:'#193255', fontSize:11, fontWeight:700, padding:'4px 10px', cursor:'pointer' }}
            onClick={onAddGroup}
          >
            {t('trainerDashboard.addGroup')} +
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:12, color:'#9ca3af' }}>{t('trainerDashboard.loadingGroups')}</div>}
      {!loading && err && <div style={{ fontSize:12, color:'#f59e0b', marginBottom:6 }}>{err}</div>}
      {!loading && groups.length === 0 && <div style={{ fontSize:12, color:'#9ca3af' }}>{t('trainerDashboard.noGroups')}</div>}

      {!loading && groups.length > 0 && (
        <div style={{ maxHeight:320, overflowY:'auto' }}>
          {groups.map(g => {
            const open = !!expanded[g._id];
            const count = g.members?.length || 0;
            return (
              <div key={g._id} style={{ marginBottom:6, border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
                <div
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', background:'#f8fafc', cursor:'pointer' }}
                  onClick={() => setExpanded(prev => ({ ...prev, [g._id]: !open }))}
                >
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:10 }}>{open ? '▾' : '▸'}</span>
                    <span style={{ fontWeight:700, fontSize:13 }}>{g.name}</span>
                  </div>
                  <span style={{ background:'#193255', color:'#fff', borderRadius:999, padding:'2px 8px', fontSize:10, fontWeight:700 }}>
                    {count}
                  </span>
                </div>

                {open && (
                  <ul style={{ listStyle:'none', margin:0, padding:0 }}>
                    {count === 0 && (
                      <li style={{ padding:'6px 10px', fontSize:12, color:'#9ca3af' }}>{t('trainerDashboard.noMembers')}</li>
                    )}
                    {g.members?.map(id => {
                      const a = athIdx.get(String(id));
                      if (!a) return null;
                      return (
                        <li key={id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', borderTop:'1px solid #f1f5f9' }}>
                          <img src={a.avatar} alt={a.name} width={26} height={26}
                            style={{ borderRadius:'50%', objectFit:'cover', flexShrink:0 }}
                            onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                          />
                          <span style={{ fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={a.name}>{a.name}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
